/**
 * Interface providing a set of methods to control
 * a player instance.
 */

import type { VideoElementWrapper } from '@core/video/videoElementWrapper/wrappers/bases/VideoElementWrapper'
import type {
  PlayerInterruptCallbackFn,
  PlayerInterruptEvent,
} from '@player/features/interrupts/types'

import type { IPlayer, IPlayerInstance } from './types'

/**
 * PlayerControl is a class that provides a set of methods to programmatically control
 * a player instance via `_fwn.player` or `_fwn.players`.
 */
export class PlayerControl implements IPlayer {
  #instance: IPlayerInstance

  /**
   * Last requested mute state. Used when `videoWrapper` is not yet
   * attached so that reads/writes through `_fwn.player.muted` are not
   * silently lost in the gap between PlayerControl registration and
   * the underlying video element being ready. `undefined` means no
   * caller has set `muted` yet; in that case the getter falls back to
   * `true` (pessimistic) because browsers typically fall back to muted
   * autoplay and consumers like `restoreSoftPIPPlayer` rely on `muted`
   * being truthy to replay a deferred unmute attempt.
   */
  #requestedMuted: boolean | undefined

  /**
   *
   */
  constructor(instance: IPlayerInstance) {
    this.#instance = instance
  }

  /**
   * Re-point this PlayerControl at a new internal instance. Used when
   * the surrounding React component re-registers (e.g. the underlying
   * videoEl becomes available after initial registration). Any
   * previously requested mute state is preserved and re-applied to the
   * new `videoWrapper` so a `_fwn.player.muted = X` write issued before
   * the wrapper attached is not silently lost.
   */
  updateInstance(instance: IPlayerInstance) {
    this.#instance = instance
    if (instance.videoWrapper && this.#requestedMuted !== undefined) {
      instance.videoWrapper.muted = this.#requestedMuted
    }
  }

  /**
   * Get the name of the widget the player belongs to (was initialized by).
   */
  get name() {
    return this.#instance.name
  }

  /**
   * Provide read only access to API video object
   */
  get video() {
    return this.#instance.video
  }

  /**
   *
   */
  get currentTime() {
    return this.#instance.videoWrapper?.currentTime || 0
  }

  /**
   *
   */
  set currentTime(currentTime) {
    if (this.#instance.videoWrapper) {
      this.#instance.videoWrapper.currentTime = currentTime
    }
  }

  /**
   *
   */
  get muted() {
    return this.#instance.videoWrapper?.muted ?? this.#requestedMuted ?? true
  }

  /**
   *
   */
  set muted(value) {
    this.#requestedMuted = value
    if (this.#instance.videoWrapper) {
      this.#instance.videoWrapper.muted = value
    }
  }

  /**
   *
   */
  get textTracks() {
    return (
      (this.#instance.videoWrapper as VideoElementWrapper | undefined)
        ?.textTracks ?? new TextTrackList()
    )
  }

  /**
   *
   */
  mute() {
    this.#instance.handleMute()
  }

  /**
   *
   */
  unmute() {
    this.#instance.handleUnmute()
  }

  /**
   *
   */
  close() {
    this.#instance.handleClose()
  }

  /**
   *
   */
  minimize() {
    this.#instance.handleMinimize()
  }

  /**
   *
   */
  fullscreen() {
    this.#instance.handleMaximize()
  }

  /**
   *
   */
  play() {
    this.#instance.handlePlay()
  }

  /**
   *
   */
  pause() {
    this.#instance.handlePause()
  }

  /**
   * Register an async callback to be called when an event occurs.
   */
  on(event: PlayerInterruptEvent, fn: PlayerInterruptCallbackFn) {
    this.#instance.registerCallback(event, fn)
  }

  /**
   * Unregister a callback from an event.
   */
  off(event: PlayerInterruptEvent) {
    this.#instance.unregisterCallback(event)
  }
}
